import { useEffect, useState, useRef, useMemo } from 'react';
import { Views, type View } from 'react-big-calendar';
import { startOfMonth, endOfMonth } from 'date-fns';
import { CalendarDaysIcon } from '@heroicons/react/24/outline';
import { useAppStore } from '@/stores/appStore';
import { Navbar } from '@/components/layout/Navbar';
import { CalendarView } from '@/components/calendar/CalendarView';
import { FilterBar } from '@/components/filters/FilterBar';
import {
  productApi,
  subProductApi,
  initiativeApi,
  classificationApi,
  statusApi,
  tagApi,
  userApi,
  fiscalYearApi,
  fieldConfigApi,
  labsApi,
} from '@/services/api';

// Parse a stored ISO datetime as a LOCAL calendar day (avoids the UTC-midnight → previous-day
// shift in timezones west of UTC), matching how CalendarView places events.
function parseLocalDate(iso: string): Date {
  const [y, m, d] = iso.slice(0, 10).split('-').map(Number);
  return new Date(y, (m || 1) - 1, d || 1);
}

export function CalendarPage() {
  const {
    setProducts,
    setInitiatives,
    setClassifications,
    setStatuses,
    setTags,
    setOwners,
    setQuarters,
    setFieldConfigs,
    setLabsFeatures,
    setLoading,
    setError,
    refreshTrigger,
    quarters,
    activeSavedView,
  } = useAppStore();

  const [filteredInitiatives, setFilteredInitiatives] = useState<any[]>([]);
  const [dataLoading, setDataLoading] = useState(true);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [view, setView] = useState<View>(Views.MONTH);
  const [date, setDate] = useState<Date>(new Date());
  // Default ON: campaign-style (date-mode) work is what the calendar is for; showing all the
  // month-mode records fills every day with full-month bars. Persisted per browser.
  const [onlyWithDates, setOnlyWithDates] = useState<boolean>(() => {
    const v = localStorage.getItem('cpr_calendar_only_dates');
    return v === null ? true : v === 'true';
  });
  const viewContainerRef = useRef<HTMLDivElement>(null);

  const toggleOnlyWithDates = () =>
    setOnlyWithDates((prev) => {
      const next = !prev;
      localStorage.setItem('cpr_calendar_only_dates', String(next));
      return next;
    });

  // The initiatives actually shown on the calendar (respecting the "Only with dates" toggle).
  const visibleInitiatives = useMemo(
    () => (onlyWithDates ? filteredInitiatives.filter((i) => i.datePrecision === 'day') : filteredInitiatives),
    [onlyWithDates, filteredInitiatives]
  );

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      setDataLoading(true);

      const [
        productsData,
        subProductsData,
        initiativesData,
        classificationsData,
        statusesData,
        tagsData,
        usersData,
        fiscalYearsData,
        fieldConfigData,
        labsData,
      ] = await Promise.all([
        productApi.getAll(),
        subProductApi.getAll(),
        initiativeApi.getAll(),
        classificationApi.getAll(),
        statusApi.getAll(),
        tagApi.getAll(),
        userApi.getAll(),
        fiscalYearApi.getAll(),
        fieldConfigApi.getAll().catch(() => []),
        labsApi.getAll().catch(() => []),
      ]);

      const productsWithSubProducts = productsData.map((product: any) => ({
        ...product,
        subProducts: subProductsData.filter((sp: any) => sp.productId === product.id),
      }));

      setProducts(productsWithSubProducts);
      setInitiatives(initiativesData);
      setClassifications(classificationsData);
      setStatuses(statusesData);
      setTags(tagsData);
      setOwners(usersData);
      if (fieldConfigData.length > 0) {
        setFieldConfigs(fieldConfigData);
      }
      if (labsData.length > 0) {
        setLabsFeatures(labsData);
      }

      const allQuarters = fiscalYearsData
        .flatMap((fy: any) => fy.quarters || [])
        .sort((a: any, b: any) => new Date(a.startDate).getTime() - new Date(b.startDate).getTime());

      setQuarters(allQuarters);
    } catch (err) {
      console.error('Failed to load data:', err);
      setError('Failed to load roadmap data');
    } finally {
      setLoading(false);
      setDataLoading(false);
      setHasLoaded(true);
    }
  };

  useEffect(() => {
    loadData();
  }, [refreshTrigger]);

  // When the visible set changes (search/filter/toggle) and none of the matches fall in the
  // currently visible month, jump the calendar to the earliest match — otherwise a search can
  // appear to "find nothing" simply because the record sits in a different month. Only runs on
  // set changes, so it never hijacks Back/Next browsing to empty months.
  useEffect(() => {
    if (!hasLoaded) return;
    if (visibleInitiatives.length === 0) return;

    const rangeOf = (init: any): { start: Date; end: Date } | null => {
      const sIso = init.startDate || quarters.find((q: any) => q.key === init.startQuarter)?.startDate;
      if (!sIso) return null;
      const start = parseLocalDate(sIso);
      const eIso =
        init.endDate ||
        (init.endQuarter ? quarters.find((q: any) => q.key === init.endQuarter)?.endDate : null);
      const end = eIso ? parseLocalDate(eIso) : start;
      return { start, end: end < start ? start : end };
    };

    const ranges = visibleInitiatives.map(rangeOf).filter((r): r is { start: Date; end: Date } => r !== null);
    if (ranges.length === 0) return;

    const monthStart = startOfMonth(date);
    const monthEnd = endOfMonth(date);
    const anyVisible = ranges.some((r) => r.start <= monthEnd && r.end >= monthStart);
    if (!anyVisible) {
      const earliest = ranges.reduce((a, b) => (a.start < b.start ? a : b));
      setDate(earliest.start);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visibleInitiatives]);

  return (
    <div className="h-screen flex flex-col bg-background-primary">
      <Navbar />

      <main className="flex-1 overflow-hidden">
        <div className="h-full flex flex-col">
          <div className="px-3 sm:px-6 py-3 bg-background-primary flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3">
            <div className="flex-1 min-w-0">
              <FilterBar
                key={activeSavedView?.viewType === 'calendar' ? activeSavedView.id : 'default'}
                onFilteredInitiativesChange={setFilteredInitiatives}
                viewType="calendar"
                viewContainerRef={viewContainerRef}
                savedViewKey="calendar"
                hideExport
                injectedFilterState={activeSavedView?.viewType === 'calendar' ? activeSavedView.filterState : null}
              />
            </div>
            {/* Only-with-dates toggle — hides month-mode records so the grid isn't flooded */}
            <button
              onClick={toggleOnlyWithDates}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-colors flex-shrink-0 ${
                onlyWithDates
                  ? 'bg-blue-600 text-white hover:bg-blue-700'
                  : 'bg-gray-100 text-text-secondary hover:bg-gray-200 hover:text-text-primary'
              }`}
              title="Show only initiatives entered with exact start/end dates"
            >
              <CalendarDaysIcon className="w-4 h-4" />
              <span className="hidden xl:inline">Only with dates</span>
            </button>
          </div>

          <div ref={viewContainerRef} className="flex-1 overflow-hidden">
            {dataLoading && !hasLoaded ? (
              <div className="h-full flex items-center justify-center text-text-secondary text-sm">Loading...</div>
            ) : (
              <CalendarView
                initiatives={visibleInitiatives}
                view={view}
                onViewChange={setView}
                date={date}
                onNavigate={setDate}
              />
            )}
          </div>
        </div>
      </main>
    </div>
  );
}
